import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Grid, makeStyles, CircularProgress } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles({
  swaggerContainer: {
    '& .swagger-ui': {
      fontFamily: 'inherit',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  error: {
    color: '#d32f2f',
    padding: '12px',
    fontSize: '1.1rem',
  },
  content: {
    fontSize: '1.1rem',
    padding: '5px',
  },
});

export function ApiDefinitionPage() {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { fetch: fetchApi } = useApi(fetchApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  
  const [spec, setSpec] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const openApiSpecificationUrl = (
    entity.metadata.annotations as { 'backstage.io/openApiSpecificationUrl'?: string }
  )?.['backstage.io/openApiSpecificationUrl'];

  useEffect(() => {
    if (!openApiSpecificationUrl) {
      setLoading(false);
      return;
    }

    const fetchSpec = async () => {
      try {
        setLoading(true);
        setError(null);

        // Call the backend proxy endpoint (similar to PowerBI pattern)
        const url = `${backendUrl}/api/openapi/spec?url=${encodeURIComponent(openApiSpecificationUrl)}`;
        
        const response = await fetchApi(url);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to fetch spec: ${response.status}`);
        }

        const specContent = await response.text();

        // Try to parse as JSON, otherwise treat as YAML
        let parsedSpec;
        try {
          parsedSpec = JSON.parse(specContent);
        } catch {
          // SwaggerUI can handle YAML strings directly
          parsedSpec = specContent;
        }

        setSpec(parsedSpec);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load OpenAPI specification');
        console.error('Error fetching OpenAPI spec:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSpec();
  }, [openApiSpecificationUrl, backendUrl, fetchApi]);

  if (!openApiSpecificationUrl) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InfoCard>
            <div className={classes.content}>
              <p>No OpenAPI specification URL found for this API.</p>
              <p>Add the <code>backstage.io/openApiSpecificationUrl</code> annotation to your entity metadata.</p>
            </div>
          </InfoCard>
        </Grid>
      </Grid>
    );
  }

  if (loading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InfoCard>
            <div className={classes.error}>
              <p><strong>Error loading OpenAPI specification:</strong></p>
              <p>{error}</p>
              <p style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}>
                URL: {openApiSpecificationUrl}
              </p>
            </div>
          </InfoCard>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div className={classes.swaggerContainer}>
          <SwaggerUI spec={spec} />
        </div>
      </Grid>
    </Grid>
  );
}
